import React, { useState } from 'react';
import useFetch from 'use-http';
import Dialog from 'src/components/common/Dialog';
import Panel from 'src/components/common/Panel';
import Button from 'src/components/controls/forms/redesign/FormButton';
import DialogList from 'src/dialogs';

interface IAp1099ReportDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
}

const downloadBlobResponseAsFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'document';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// Ports ap_1099.fmt's Standard screen's Report Name dropdown + OK button - the same filter fields
// already on that canvas (Tax Year/Beg-End No./No Tax Ids/Min-Max Amount) drive whichever of the
// four reports is picked. Client Number is deliberately left out - confirmed dead in every one of
// the four reports' own WHERE_CLAUSE construction. Each report has a different @Parameters shape
// server-side (see ReportService.GetAp1099ReportFile) - the frontend just sends named fields and
// lets the backend build the right positional string per report.
const Ap1099ReportDialog = (props: IAp1099ReportDialogProps) => {
    const { post, response } = useFetch();
    const [reportName, setReportName] = useState<string>('AP_1099_Audit');
    const [reportTaxYear, setReportTaxYear] = useState<string>(`${new Date().getFullYear() - 1}`);
    const [reportBegNo, setReportBegNo] = useState<string>('');
    const [reportEndNo, setReportEndNo] = useState<string>('');
    const [reportNoTaxIds, setReportNoTaxIds] = useState<string>('');
    const [reportMinAmt, setReportMinAmt] = useState<string>('');
    const [reportMaxAmt, setReportMaxAmt] = useState<string>('');
    const [generatingReport, setGeneratingReport] = useState<boolean>(false);
    const [message, setMessage] = useState<{ text: string; color?: string } | null>(null);

    const handleGenerateReport = async () => {
        if (!reportTaxYear) {
            setMessage({ text: 'Enter a Tax Year for the report.', color: 'rgba(255,0,0,0.5)' });
            return;
        }

        setGeneratingReport(true);
        await post('/v1.0/reports/ap1099', {
            reportName,
            taxYear: reportTaxYear,
            begNo: reportBegNo,
            endNo: reportEndNo,
            noTaxIds: reportNoTaxIds,
            minAmt: reportMinAmt,
            maxAmt: reportMaxAmt
        });

        if (response.ok) {
            const blob = await response.blob();
            downloadBlobResponseAsFile(blob, `${reportName}.pdf`);
        } else {
            setMessage({ text: 'Unable to generate report.', color: 'rgba(255,0,0,0.5)' });
        }
        setGeneratingReport(false);
    };

    // Returns to the 1099 Maintenance dialog rather than closing outright, matching
    // CompanyMaintenanceDialog.tsx's own Back button and Oracle's own Standard-screen return flow.
    const back = () => {
        props.showDialog(DialogList.AP_1099_MAINTENANCE);
    };

    return (
        <Dialog title="1099 Reports" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '30%', maxWidth: '500px' }}>
            <Panel backgroundColor="#D3D3D3">
                <Panel panelTitle="Reports">
                    <div style={{ padding: '.5rem' }}>
                        <div className="form-row" style={{ alignItems: 'center', marginBottom: '.5rem' }}>
                            <label style={{ marginRight: '.5rem', width: '7rem' }}>Report Name</label>
                            <select
                                value={reportName}
                                onChange={(e) => setReportName(e.currentTarget.value)}
                                style={{ height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', minWidth: '14rem' }}
                            >
                                <option value="AP_1099_MISC">A/P Vendor 1099 - MISC</option>
                                <option value="AP_1099_NEC">A/P Vendor 1099 - NEC</option>
                                <option value="AP_1099_Audit">A/P Vendor 1099 Audit File</option>
                                <option value="AP_1099_Pay">A/P Vendor Payments</option>
                            </select>
                        </div>
                        <div className="form-row" style={{ alignItems: 'center', marginBottom: '.5rem' }}>
                            <label style={{ marginRight: '.5rem', width: '7rem' }}>Tax Year</label>
                            <input
                                type="number"
                                value={reportTaxYear}
                                onChange={(e) => setReportTaxYear(e.currentTarget.value)}
                                style={{ width: '6rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '2rem' }}
                            />
                            <label style={{ marginRight: '.5rem' }}>No Tax Ids</label>
                            <select
                                value={reportNoTaxIds}
                                onChange={(e) => setReportNoTaxIds(e.currentTarget.value)}
                                style={{ height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem' }}
                            >
                                <option value="">No</option>
                                <option value="YES">Yes</option>
                            </select>
                        </div>
                        <div className="form-row" style={{ alignItems: 'center', marginBottom: '.5rem' }}>
                            <label style={{ marginRight: '.5rem', width: '7rem' }}>Beginning No.</label>
                            <input
                                type="number"
                                value={reportBegNo}
                                onChange={(e) => setReportBegNo(e.currentTarget.value)}
                                style={{ width: '8rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '2rem' }}
                            />
                            <label style={{ marginRight: '.5rem' }}>Ending Vendor No.</label>
                            <input
                                type="number"
                                value={reportEndNo}
                                onChange={(e) => setReportEndNo(e.currentTarget.value)}
                                style={{ width: '8rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem' }}
                            />
                        </div>
                        <div className="form-row" style={{ alignItems: 'center', marginBottom: '.5rem' }}>
                            <label style={{ marginRight: '.5rem', width: '7rem' }}>Minimum Amount</label>
                            <input
                                type="number"
                                value={reportMinAmt}
                                onChange={(e) => setReportMinAmt(e.currentTarget.value)}
                                style={{ width: '8rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem', marginRight: '2rem' }}
                            />
                            <label style={{ marginRight: '.5rem' }}>Maximum Amount</label>
                            <input
                                type="number"
                                value={reportMaxAmt}
                                onChange={(e) => setReportMaxAmt(e.currentTarget.value)}
                                style={{ width: '8rem', height: '38px', border: '1px solid #ccc', borderRadius: '4px', padding: '0 .5rem' }}
                            />
                        </div>

                        {message && (
                            <div style={{ padding: '.5rem 0', color: message.color ? '#a00' : '#060' }}>{message.text}</div>
                        )}

                        <div className="form-row" style={{ justifyContent: 'center', marginTop: '1rem' }}>
                            <Button
                                text={generatingReport ? 'Generating...' : 'OK'}
                                className="blue-hover-button"
                                disabled={generatingReport}
                                style={{ width: '8rem', marginRight: '1rem' }}
                                onClick={() => handleGenerateReport()}
                            />
                            <Button
                                text="Back"
                                className="blue-hover-button"
                                disabled={generatingReport}
                                style={{ width: '8rem' }}
                                onClick={() => back()}
                            />
                        </div>
                    </div>
                </Panel>
            </Panel>
        </Dialog>
    );
};

export default Ap1099ReportDialog;



// WEBPACK FOOTER //
// ./src/components/accounts-payable/dialogs/Ap1099ReportDialog.tsx